import {
  FRAKTIONEN,
  type Fraktion,
} from "@/components/atomic-crm/misc/fraktionen";

import { HUB_ORIGIN } from "../navigation/hubRedirect";
import type { VoltRoles } from "../navigation/voltAreas";
import type { VoltStatusMap } from "../ui/voltStatus";
import type {
  AttendanceAudit,
  AttendanceClosingStatus,
  AttendanceEntry,
  AttendanceSession,
  AttendanceStatus,
} from "./types";

export const TIME_ZONE = "Europe/Berlin";

export const STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: "Anwesend",
  excused: "Entschuldigt",
  absent: "Abwesend",
  open: "Offen",
};

export const STATUS_MAP: VoltStatusMap = {
  present: { tone: "success", label: STATUS_LABELS.present },
  excused: { tone: "info", label: STATUS_LABELS.excused },
  absent: { tone: "destructive", label: STATUS_LABELS.absent },
  open: { tone: "neutral", label: STATUS_LABELS.open },
};

export const KIND_LABELS = {
  big: "Große Liste",
  small: "Kleine Liste · Vorstand",
} as const;

const isFraktion = (v: string | null): v is Fraktion =>
  FRAKTIONEN.some((f) => f.value === v);

/** Own Fraktion only, for admins and superadmins alike; RLS draws the same line. */
export const attendanceDistrict = (
  roles: VoltRoles | null,
): Fraktion | null => {
  return isFraktion(roles?.fraktion ?? null)
    ? (roles!.fraktion as Fraktion)
    : null;
};

/** Closings that still have to go to the BV: waiting for approval, approved, or drafted. */
export const CLOSING_PENDING: AttendanceClosingStatus[] = [
  "review",
  "approved",
  "drafted",
];

/** Big list: every session that is not cancelled; small list: the one picked as billable. */
export const goesToBv = (
  session: Pick<AttendanceSession, "id" | "kind" | "cancelled">,
  billableId: number | null | undefined,
) => (session.kind === "big" ? !session.cancelled : session.id === billableId);

export const checkInUrl = (code: string) =>
  `${HUB_ORIGIN}/#/check-in/${encodeURIComponent(code)}`;

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** Month of a timestamp in Berlin time, as YYYY-MM. */
export const berlinMonth = (iso: string | Date) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}`;
};

/** The ?month= parameter if valid, otherwise the current Berlin month. */
export const parseMonth = (
  param: string | null | undefined,
  now = new Date(),
) => (param && MONTH.test(param) ? param : berlinMonth(now));

export const shiftMonth = (month: string, delta: number) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

/** Query window wide enough for every Berlin offset; rows are then kept by berlinMonth. */
export const monthQueryRange = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  return {
    from: new Date(Date.UTC(y, m - 1, 1) - 86_400_000).toISOString(),
    to: new Date(Date.UTC(y, m, 1) + 86_400_000).toISOString(),
  };
};

export const monthLabel = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, 15)));
};

export const monthName = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, 15)));
};

const fmt = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("de-DE", { timeZone: TIME_ZONE, ...options });

export const formatTime = (iso: string) =>
  fmt({ hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
export const formatDate = (iso: string) =>
  fmt({ weekday: "short", day: "2-digit", month: "2-digit" }).format(
    new Date(iso),
  );
export const formatLongDate = (iso: string) =>
  fmt({
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(iso));
export const formatDateTime = (iso: string) =>
  fmt({
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

/** "vor 4 Min.", "vor 3 Std.", "vor 2 Tagen"; null without a timestamp. */
export const syncedAgo = (iso: string | null, now = new Date()) => {
  if (!iso) return null;
  const minutes = Math.max(
    0,
    Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000),
  );
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "vor 1 Tag" : `vor ${days} Tagen`;
};

export type AttendanceCounts = Record<AttendanceStatus, number> & {
  invited: number;
};

/** Counts over invitees and admitted additions; an unanswered late request is not counted yet. */
export const countEntries = (
  entries: Pick<AttendanceEntry, "status" | "invited">[],
): AttendanceCounts => {
  const counts: AttendanceCounts = {
    present: 0,
    excused: 0,
    absent: 0,
    open: 0,
    invited: 0,
  };
  for (const e of entries) {
    if (!e.invited && e.status === "open") continue;
    counts[e.status] += 1;
    counts.invited += 1;
  }
  return counts;
};

/** A late request is open until an admin decides it or sets a status after it. */
export const isOpenLateRequest = (
  entry: Pick<AttendanceEntry, "id" | "status" | "requested_at">,
  audit: Pick<AttendanceAudit, "entity" | "entity_id" | "action" | "at">[],
) => {
  if (!entry.requested_at || entry.status === "present") return false;
  const asked = new Date(entry.requested_at).getTime();
  return !audit.some(
    (a) =>
      a.entity === "entry" &&
      a.entity_id === entry.id &&
      (a.action === "set_status" || a.action === "late_decision") &&
      new Date(a.at).getTime() >= asked,
  );
};

/** The "Woher" column: where the current status comes from. */
export const entryOrigin = (
  entry: Pick<
    AttendanceEntry,
    "id" | "status" | "source" | "checked_in_at" | "requested_at"
  >,
  audit: Pick<AttendanceAudit, "entity" | "entity_id" | "action" | "at">[],
) => {
  if (entry.source === "check_in" && entry.checked_in_at)
    return `QR ${formatTime(entry.checked_in_at)}`;
  if (entry.source === "manual") {
    const last = audit
      .filter(
        (a) =>
          a.entity === "entry" &&
          a.entity_id === entry.id &&
          (a.action === "set_status" ||
            a.action === "add_person" ||
            a.action === "late_decision"),
      )
      .at(-1);
    const prefix =
      last?.action === "add_person"
        ? "Hinzugefügt"
        : last?.action === "late_decision"
          ? "Nachtrag entschieden"
          : "Korrektur";
    return last ? `${prefix} ${formatDateTime(last.at)}` : prefix;
  }
  if (entry.requested_at && entry.status !== "present")
    return `Nachtrag angefragt ${formatDateTime(entry.requested_at)}`;
  if (entry.source === "calendar" && entry.status === "excused")
    return "Absage im Kalender";
  if (entry.source === "auto") return "nicht eingecheckt";
  return "noch nicht eingecheckt";
};

/** Upload path of a new signature image; matches the storage insert policy. */
export const signaturePath = (district: string, file: File, now = Date.now()) =>
  `${district}/signature-${now}.${file.type === "image/png" ? "png" : "jpg"}`;

const statusOf = (row: Record<string, unknown> | null) =>
  STATUS_LABELS[row?.status as AttendanceStatus] ?? String(row?.status ?? "");

/** One line of the audit trail; name() resolves a sales id. */
export const auditText = (
  a: Pick<AttendanceAudit, "entity" | "action" | "old" | "new">,
  name: (salesId: number | null) => string,
) => {
  const who = name(Number((a.new ?? a.old)?.sales_id ?? Number.NaN) || null);
  const change = `${statusOf(a.old)} → ${statusOf(a.new)}`;
  if (a.entity === "session") {
    const texts: Record<string, string> = {
      sync_insert: "Aus dem Kalender übernommen",
      sync_update: "Im Kalender geändert",
      sync_missing: "Im Kalender nicht mehr gefunden",
      set_billable: "Als Sitzung des Monats an die BV gesetzt",
    };
    return texts[a.action] ?? a.action;
  }
  switch (a.action) {
    case "check_in":
      return `${who} per QR eingecheckt`;
    case "set_status":
      return `${who}: ${change}`;
    case "late_request":
      return `${who} hat einen Nachtrag angefragt`;
    case "late_decision":
      return a.new?.status === "present"
        ? `Nachtrag von ${who} bestätigt`
        : `Nachtrag von ${who} abgelehnt`;
    case "add_person":
      return `${who} hinzugefügt als ${statusOf(a.new)}`;
    case "sync_insert":
      return `${who} laut Kalender eingeladen`;
    case "sync_update":
      return a.old?.status !== a.new?.status
        ? `${who}: ${change} (Kalender)`
        : `${who}: im Kalender geändert`;
    case "sync_remove":
      return `${who} nicht mehr eingeladen (Kalender)`;
    default:
      return `${who}: ${a.action}`;
  }
};
